// Map-control analytics for the Analysis view.
//
// Three views over the filtered rounds, chosen because each is honest at the sample size an amateur
// league actually provides (a team's main map is ~100-220 rounds a season, ~50-110 per side; a single
// match is 12-16):
//
//   Areas   - take rate and first-take timing per callout, plus ground conceded with no fight for it
//   Rounds  - one row per round, who owned a chosen area each second. A log, so it never over-claims
//   Share   - territory share over time, one line per round
//
// Side is read from the demo roster only (rosterSideForTeam in roundSide.ts), never from the round
// number: team1 doesn't reliably start CT, so a guessed side would flip whole halves.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMapGeometry } from '../../services/PlayerAreaService';
import { loadControlGrid, hasControlGrid } from '../MatchPage/Replay/mapControl/mapControlGrid';
import type { ControlGrid } from '../MatchPage/Replay/mapControl/mapControlGrid';
import { multiLevelMaps, mapImageMappings } from '../MatchPage/Replay/replayRenderer';
import type { ParsedReplay } from '../MatchPage/Replay/replayParser';
import {
    buildCellAreas, computeRoundReductions, playersInRegion, OWN_T, OWN_CT, OWN_CONTEST,
    BUCKET_SECS, NUM_BUCKETS,
    type AreaShape, type RoundReduction, type RoundInput,
} from './mapControlAggregate';

const T_COL = '#de9b35', CT_COL = '#5d79ae', CONTEST_COL = '#b84448', NONE_COL = '#2a2e3a';

export interface MapControlRound {
    key: string;
    roundNumber: number;
    replay: ParsedReplay;
    ourSide: 'T' | 'CT';
    won: boolean | null;
}

interface Props {
    mapName: string;
    rounds: MapControlRound[];
    teamName?: string;
    /** open a round in the replay inspector, by its loadedRounds key */
    onOpenRound?: (key: string) => void;
    playerNames?: Record<string, string>;
}

type Tab = 'map' | 'areas' | 'rounds' | 'share';

const ROUND_CAP_DEFAULT = 30;
// Below this many walkable cells a callout cannot carry a meaningful take rate.
const MIN_AREA_CELLS = 25;

export const MapControlPanel: React.FC<Props> = ({ mapName, rounds, teamName, onOpenRound, playerNames }) => {
    const [tab, setTab] = useState<Tab>('map');
    const [mapMode, setMapMode] = useState<MapMode>('ownership');
    const [cellList, setCellList] = useState<Int32Array | null>(null);
    const [timeWindow, setTimeWindow] = useState<[number, number] | null>(null);
    const [selection, setSelection] = useState<number[]>([]);
    const [side, setSide] = useState<'T' | 'CT'>('T');
    const [cap, setCap] = useState(ROUND_CAP_DEFAULT);
    const [areas, setAreas] = useState<AreaShape[] | null>(null);
    const [grid, setGrid] = useState<ControlGrid | null>(null);
    const [reductions, setReductions] = useState<RoundReduction[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [error, setError] = useState<string | null>(null);
    const [areaIdx, setAreaIdx] = useState(0);
    const [activeAreas, setActiveAreas] = useState<AreaShape[] | null>(null);
    const cancelRef = useRef<{ cancelled: boolean } | null>(null);

    const ml = multiLevelMaps[mapName];
    const gridName = ml ? `${mapName}_upper` : mapName;
    const supported = hasControlGrid(gridName);

    // grid + callout geometry
    useEffect(() => {
        let dead = false;
        setReductions(null); setAreas(null); setGrid(null); setError(null);
        if (!supported) return;
        Promise.all([loadControlGrid(gridName), getMapGeometry(gridName).catch(() => null)])
            .then(([g, geo]) => {
                if (dead) return;
                if (!g) { setError(`No control grid for ${gridName}`); return; }
                setGrid(g);
                // The geometry endpoint calls it `areaName`; normalise before anything reads `.name`.
                const list = ((geo as any)?.areas ?? []) as Array<Record<string, any>>;
                const shaped: AreaShape[] = list.map(a => ({
                    name: a.areaName ?? a.name,
                    points: a.points,
                    minX: a.minX, maxX: a.maxX, minY: a.minY, maxY: a.maxY,
                    centerX: a.centerX, centerY: a.centerY, radius: a.radius,
                }));
                // Spawns are not contested ground and would dominate any share; drop them.
                setAreas(shaped.filter(a => a.name && !/spawn/i.test(a.name)));
            })
            .catch(e => { if (!dead) setError(String(e?.message ?? e)); });
        return () => { dead = true; };
    }, [gridName, supported]);

    const sideRounds = useMemo(() => rounds.filter(r => r.ourSide === side), [rounds, side]);
    // reductions hold BOTH sides; views take the slice for the side on screen
    const shown = useMemo(() => (reductions ?? []).filter(r => r.ourSide === side), [reductions, side]);

    const run = useCallback(async () => {
        if (!grid || !areas) return;
        setBusy(true); setError(null);
        const signal = { cancelled: false };
        cancelRef.current = signal;
        try {
            const cells = (() => {
                const { G, data } = grid;
                const list: number[] = [];
                for (let c = 0; c < G * G; c++) if (data[c] & 1) list.push(c);
                return Int32Array.from(list);
            })();
            const rawCellArea = buildCellAreas(grid, cells, areas);
            // Some callouts resolve to almost no walkable ground once rasterised - on nuke's upper
            // plane "Silo" lands on 4 cells - and a take rate over 4 cells is noise wearing a name.
            // Drop them rather than show them.
            const counts = new Int32Array(areas.length);
            for (let i = 0; i < rawCellArea.length; i++) if (rawCellArea[i] >= 0) counts[rawCellArea[i]]++;
            const keep: number[] = [];
            for (let a = 0; a < areas.length; a++) if (counts[a] >= MIN_AREA_CELLS) keep.push(a);
            const remap = new Int16Array(areas.length).fill(-1);
            keep.forEach((a, i) => { remap[a] = i; });
            const usedAreas = keep.map(a => areas[a]);
            const cellArea = new Int16Array(rawCellArea.length);
            for (let i = 0; i < rawCellArea.length; i++) cellArea[i] = rawCellArea[i] >= 0 ? remap[rawCellArea[i]] : -1;
            setActiveAreas(usedAreas); setCellList(cells);
            const zSplit = ml ? (ml.zUpper + ml.zLower) / 2 : undefined;
            // Both sides in ONE pass, capped per side. Computing only the visible side meant
            // flipping T/CT threw the result away and made you sit through it again, and the engine
            // has to walk the same rounds either way.
            const chosen = [
                ...rounds.filter(r => r.ourSide === 'T').slice(0, cap),
                ...rounds.filter(r => r.ourSide === 'CT').slice(0, cap),
            ];
            setProgress({ done: 0, total: chosen.length });
            const inputs: RoundInput[] = chosen.map(r => ({
                key: r.key, roundNumber: r.roundNumber, replay: r.replay, ourSide: r.ourSide, won: r.won,
            }));
            const res = await computeRoundReductions(inputs, {
                grid, cellArea, areaCount: usedAreas.length,
                zMin: zSplit, signal,
                onProgress: (done, total) => setProgress({ done, total }),
            });
            if (!signal.cancelled) setReductions(res);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setBusy(false);
        }
    }, [grid, areas, rounds, cap, ml]);

    useEffect(() => () => { if (cancelRef.current) cancelRef.current.cancelled = true; }, []);

    const areaStats = useMemo(() => {
        if (!shown.length || !activeAreas) return [];
        const mine = side === 'T' ? 'T' : 'CT';
        return activeAreas.map((a, i) => {
            const takes: number[] = [];
            let conceded = 0, fromStart = 0;
            for (const r of shown) {
                const first = mine === 'T' ? r.firstTakeT[i] : r.firstTakeCT[i];
                if (first === -2) fromStart++;          // already theirs at the whistle
                else if (first >= 0) takes.push(first);
                if (r.concededUncontested[i]) conceded++;
            }
            const sorted = [...takes].sort((x, y) => x - y);
            const median = sorted.length ? sorted[sorted.length >> 1] : null;
            return {
                name: a.name, idx: i, taken: takes.length, of: shown.length,
                median, takes: sorted, conceded, fromStart,
                // Several callouts are stored as a bounding box rather than a polygon, so their
                // edges bleed into neighbouring ground. Mark them instead of pretending otherwise.
                approx: !(a.points && a.points.length > 2),
            };
        }).sort((x, y) => (y.taken / Math.max(1, y.of)) - (x.taken / Math.max(1, x.of)));
    }, [shown, activeAreas, side]);

    // after every hook, so the hook order never changes between renders
    if (!supported) {
        return <div style={{ padding: 24, color: '#aaa' }}>No map-control data for {mapName}.</div>;
    }

    return (
        <div style={{ padding: '12px 16px', color: '#ddd', overflowY: 'auto', height: '100%' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                {(['map', 'areas', 'rounds', 'share'] as Tab[]).map(t => (
                    <button key={t} onClick={() => setTab(t)}
                        style={{
                            padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                            border: `1px solid ${tab === t ? '#e08a2e' : '#3a3f4b'}`,
                            background: tab === t ? 'rgba(224,138,46,0.15)' : 'transparent',
                            color: tab === t ? '#e08a2e' : '#bbb', textTransform: 'capitalize',
                        }}>{t}</button>
                ))}
                <div style={{ width: 1, height: 22, background: '#3a3f4b' }} />
                {(['T', 'CT'] as const).map(s => (
                    <button key={s} onClick={() => { setSide(s); setSelection([]); }}
                        style={{
                            padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                            border: `1px solid ${side === s ? (s === 'T' ? T_COL : CT_COL) : '#3a3f4b'}`,
                            background: side === s ? (s === 'T' ? 'rgba(222,155,53,0.15)' : 'rgba(93,121,174,0.15)') : 'transparent',
                            color: side === s ? (s === 'T' ? T_COL : CT_COL) : '#bbb',
                        }}>{teamName ? `${teamName} on ${s}` : s}</button>
                ))}
                <span style={{ color: '#888', fontSize: 13 }}>
                    {reductions ? `${shown.length} rounds on ${side}` : `${sideRounds.length} rounds on ${side}`}
                    {sideRounds.length > cap && ` (capped at ${cap} per side)`}
                </span>
                <label style={{ color: '#888', fontSize: 13 }}>
                    cap{' '}
                    <input type="number" min={5} max={200} value={cap}
                        onChange={e => setCap(Math.max(5, Math.min(200, Number(e.target.value) || ROUND_CAP_DEFAULT)))}
                        style={{ width: 60, background: '#1b1e26', color: '#ddd', border: '1px solid #3a3f4b', borderRadius: 4, padding: '2px 6px' }} />
                </label>
                <button onClick={run} disabled={busy || !grid || !areas || !sideRounds.length}
                    style={{
                        padding: '6px 16px', borderRadius: 6, cursor: busy ? 'default' : 'pointer',
                        border: '1px solid #e08a2e', background: '#e08a2e', color: '#1b1e26', fontWeight: 600,
                        opacity: busy || !sideRounds.length ? 0.5 : 1,
                    }}>
                    {busy ? `Computing ${progress.done}/${progress.total}` : reductions ? 'Recompute' : 'Compute'}
                </button>
                <span style={{
                    fontSize: 11, fontWeight: 800, letterSpacing: '0.04em',
                    background: 'rgba(238,125,63,0.18)', border: '1px solid rgba(238,125,63,0.5)',
                    color: '#ee7d3f', borderRadius: 3, padding: '1px 5px',
                }} title="Alpha: the numbers here are still being validated - read them as pointers to rounds worth watching, not as settled facts.">ALPHA</span>
                {ml && <span style={{ color: '#c8a35a', fontSize: 12 }}>upper level only</span>}
            </div>

            {error && <div style={{ color: '#e07070', marginBottom: 10 }}>{error}</div>}
            {!reductions && !busy && (
                <div style={{ color: '#888', fontSize: 14, maxWidth: 720, lineHeight: 1.5 }}>
                    Runs the map-control engine over the filtered rounds and reduces each to per-area
                    control. Roughly 0.7s per round, so 30 rounds is about 20 seconds.
                </div>
            )}

            {reductions && tab === 'map' && grid && cellList && (
                <div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                        {([
                            ['ownership', 'Who owns this ground'],
                            ['contested', 'Where it is fought for'],
                            ['timing', 'When they get here'],
                            ['conceded', 'Given up without a fight'],
                        ] as Array<[MapMode, string]>).map(([m, label]) => (
                            <button key={m} onClick={() => setMapMode(m)}
                                style={{
                                    padding: '5px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 12,
                                    border: `1px solid ${mapMode === m ? '#e08a2e' : '#3a3f4b'}`,
                                    background: mapMode === m ? 'rgba(224,138,46,0.15)' : 'transparent',
                                    color: mapMode === m ? '#e08a2e' : '#bbb',
                                }}>{label}</button>
                        ))}
                    </div>
                    <TimeRange
                        maxSecs={Math.max(1, ...shown.map(r => r.seconds))}
                        window={timeWindow}
                        setWindow={setTimeWindow}
                    />
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <ControlMapView
                            reductions={shown}
                            cells={cellList}
                            grid={grid}
                            imageSrc={mapImageMappings[ml ? ml.upperImage : mapName] ?? ''}
                            side={side}
                            mode={mapMode}
                            window={timeWindow}
                            onSelect={setSelection}
                            selection={selection}
                        />
                        <div style={{ flex: '1 1 460px', minWidth: 460 }}>
                            <ContestTimeline
                                reductions={shown}
                                window={timeWindow}
                                setWindow={setTimeWindow}
                                onPickRound={onOpenRound}
                            />
                        </div>
                    </div>
                    {selection.length > 0 && (
                        <RegionPanel
                            stats={probeRegion(shown, selection, side, (key, atSecond) => {
                                const rd = rounds.find(r => r.key === key);
                                if (!rd || !grid || !cellList) return [];
                                // world bounds of the picked cells
                                const { G, cell, cfg } = grid;
                                let mnx = Infinity, mny = Infinity, mxx = -Infinity, mxy = -Infinity;
                                for (const i of selection) {
                                    const c = cellList[i], gx = c % G, gy = (c - (c % G)) / G;
                                    const wx = cfg.pos_x + gx * cell, wy = cfg.pos_y - gy * cell;
                                    mnx = Math.min(mnx, wx); mxx = Math.max(mxx, wx + cell);
                                    mny = Math.min(mny, wy - cell); mxy = Math.max(mxy, wy);
                                }
                                if (mnx === Infinity) return [];
                                const zSplit = ml ? (ml.zUpper + ml.zLower) / 2 : undefined;
                                return playersInRegion(rd.replay, side, atSecond,
                                    { minX: mnx, maxX: mxx, minY: mny, maxY: mxy }, zSplit);
                            })}
                            side={side}
                            cellCount={selection.length}
                            nameOf={id => playerNames?.[id] ?? id.slice(-4)}
                            onOpenRound={onOpenRound}
                            onClear={() => setSelection([])}
                        />
                    )}
                    <div style={{ marginTop: 10, color: '#777', fontSize: 12, maxWidth: 760, lineHeight: 1.5 }}>
                        {selection.length === 0 && <><strong style={{ color: '#aaa' }}>Drag a box on the map</strong> to ask when that ground is taken, how long it is held, and when it is given up — round by round.<br /></>}
                        {mapMode === 'ownership' && <>Share of time each piece of ground belonged to one side across these rounds — orange T, blue CT. Deep colour means it was consistently one team's; pale means it changed hands.</>}
                        {mapMode === 'contested' && <>How often each piece of ground was actively fought over. This is the ground that decides rounds, and it is not where the kills are — it includes space taken with a gun trained down it rather than a duel.</>}
                        {mapMode === 'timing' && <>Median second at which {side} first held each piece of ground. Bright is early, dark is late. Cells held by fewer than a quarter of rounds are left blank rather than drawn from one or two samples.</>}
                        {mapMode === 'conceded' && <>Ground {side} held and then lost with effectively no fight over it. This is the view a kill heatmap cannot produce — nobody dies here, so it does not exist on a heatmap.</>}
                    </div>
                </div>
            )}
            {reductions && tab === 'areas' && (
                <AreaTable stats={areaStats} side={side} onPick={(i) => { setAreaIdx(i); setTab('rounds'); }} />
            )}
            {reductions && tab === 'rounds' && activeAreas && (
                <RoundStrip reductions={shown} areas={activeAreas} areaIdx={Math.min(areaIdx, activeAreas.length - 1)} setAreaIdx={setAreaIdx} />
            )}
            {reductions && tab === 'share' && (
                <ShareCurves reductions={shown} side={side} />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------------------------

const AreaTable: React.FC<{
    stats: Array<{ name: string; idx: number; taken: number; of: number; median: number | null; takes: number[]; conceded: number; fromStart: number; approx: boolean }>;
    side: 'T' | 'CT';
    onPick: (i: number) => void;
}> = ({ stats, side, onPick }) => {
    const col = side === 'T' ? T_COL : CT_COL;
    const MAX_SEC = 75;
    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 90px 70px 1fr 150px', gap: 8, padding: '6px 4px', color: '#888', fontSize: 12, borderBottom: '1px solid #2f3440' }}>
                <div>Area</div><div>Taken</div><div>Median</div><div>When it was taken (0–{MAX_SEC}s)</div><div>Conceded uncontested</div>
            </div>
            {stats.map(s => (
                <div key={s.idx} onClick={() => onPick(s.idx)}
                    style={{ display: 'grid', gridTemplateColumns: '150px 90px 70px 1fr 150px', gap: 8, padding: '7px 4px', borderBottom: '1px solid #23262f', cursor: 'pointer', alignItems: 'center' }}>
                    <div style={{ color: '#ddd' }}>
                        {s.name}
                        {s.approx && (
                            <span title="This callout has no polygon in the map data, only a bounding box, so its edges overlap neighbouring ground."
                                style={{ color: '#8a7a4a', marginLeft: 5, cursor: 'help' }}>≈</span>
                        )}
                    </div>
                    <div style={{ color: s.fromStart >= s.of * 0.8 ? '#777' : col, fontWeight: 600 }}>
                        {s.fromStart >= s.of * 0.8 ? 'from spawn' : `${s.taken} of ${s.of - s.fromStart}`}
                    </div>
                    <div style={{ color: s.takes.length >= 8 ? '#ddd' : '#666' }}>
                        {s.median !== null && s.takes.length >= 8 ? `${s.median}s` : '—'}
                    </div>
                    <div style={{ position: 'relative', height: 18, background: '#1b1e26', borderRadius: 3 }}>
                        {s.takes.map((t, k) => (
                            <div key={k} title={`${t}s`} style={{
                                position: 'absolute', left: `${Math.min(100, (t / MAX_SEC) * 100)}%`,
                                top: 3, width: 4, height: 12, marginLeft: -2, borderRadius: 2,
                                background: col, opacity: 0.75,
                            }} />
                        ))}
                    </div>
                    <div style={{ color: s.conceded ? '#c8a35a' : '#555' }}>
                        {s.conceded ? `${s.conceded} of ${s.of} rounds` : '—'}
                    </div>
                </div>
            ))}
            <div style={{ marginTop: 12, color: '#777', fontSize: 12, maxWidth: 760, lineHeight: 1.5 }}>
                <strong>Taken</strong> means this side held most of the area outright (a body on it or a gun
                trained down it) at some point in the round, counted only over rounds where it was not already
                theirs at the whistle — spawn-adjacent callouts are marked <em>from spawn</em> rather than
                credited as a take. A <span style={{ color: '#8a7a4a' }}>≈</span> marks a callout the map data
                stores only as a bounding box, so its edges bleed into neighbouring ground and its numbers are
                looser than the rest. A median is only shown from 8 takes — below that
                the individual dots are the honest picture. <strong>Conceded uncontested</strong> counts rounds
                where the area changed hands without a single second of it being fought over.
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------------------------

const RoundStrip: React.FC<{
    reductions: RoundReduction[]; areas: AreaShape[]; areaIdx: number; setAreaIdx: (i: number) => void;
}> = ({ reductions, areas, areaIdx, setAreaIdx }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const maxSecs = useMemo(() => Math.max(1, ...reductions.map(r => r.seconds)), [reductions]);
    const sorted = useMemo(
        () => [...reductions].sort((a, b) => (b.won === true ? 1 : 0) - (a.won === true ? 1 : 0) || a.roundNumber - b.roundNumber),
        [reductions]);

    useEffect(() => {
        const cv = canvasRef.current; if (!cv) return;
        const rowH = 16, gap = 3, labelW = 54, width = 900;
        const dpr = window.devicePixelRatio || 1;
        cv.width = width * dpr; cv.height = (sorted.length * (rowH + gap) + 26) * dpr;
        cv.style.width = `${width}px`; cv.style.height = `${sorted.length * (rowH + gap) + 26}px`;
        const ctx = cv.getContext('2d'); if (!ctx) return;
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, width, cv.height);
        const plotW = width - labelW - 8;

        ctx.font = '11px system-ui, sans-serif';
        ctx.fillStyle = '#777';
        for (let s = 0; s <= maxSecs; s += 15) {
            const x = labelW + (s / maxSecs) * plotW;
            ctx.fillText(`${s}s`, x, 10);
            ctx.strokeStyle = '#262a34'; ctx.beginPath();
            ctx.moveTo(x, 14); ctx.lineTo(x, cv.height); ctx.stroke();
        }

        sorted.forEach((r, i) => {
            const y = 20 + i * (rowH + gap);
            ctx.fillStyle = r.won === true ? '#6bbf6b' : r.won === false ? '#c46a6a' : '#888';
            ctx.fillText(`R${r.roundNumber}`, 4, y + 12);
            for (let s = 0; s < r.seconds; s++) {
                const v = r.areaOwner[areaIdx * r.seconds + s];
                ctx.fillStyle = v === OWN_T ? T_COL : v === OWN_CT ? CT_COL : v === OWN_CONTEST ? CONTEST_COL : NONE_COL;
                const x0 = labelW + (s / maxSecs) * plotW;
                const x1 = labelW + ((s + 1) / maxSecs) * plotW;
                ctx.fillRect(x0, y, Math.max(1, x1 - x0 + 0.5), rowH);
            }
        });
    }, [sorted, areaIdx, maxSecs]);

    return (
        <div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {areas.map((a, i) => (
                    <button key={i} onClick={() => setAreaIdx(i)}
                        style={{
                            padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12,
                            border: `1px solid ${areaIdx === i ? '#e08a2e' : '#3a3f4b'}`,
                            background: areaIdx === i ? 'rgba(224,138,46,0.15)' : 'transparent',
                            color: areaIdx === i ? '#e08a2e' : '#bbb',
                        }}>{a.name}</button>
                ))}
            </div>
            <canvas ref={canvasRef} />
            <div style={{ marginTop: 10, display: 'flex', gap: 16, fontSize: 12, color: '#888' }}>
                <Legend c={T_COL} t="T holds" /><Legend c={CT_COL} t="CT holds" />
                <Legend c={CONTEST_COL} t="contested" /><Legend c={NONE_COL} t="nobody" />
                <span style={{ color: '#6bbf6b' }}>green round = won</span>
                <span style={{ color: '#c46a6a' }}>red = lost</span>
            </div>
        </div>
    );
};

const Legend: React.FC<{ c: string; t: string }> = ({ c, t }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 11, height: 11, background: c, borderRadius: 2 }} />{t}
    </span>
);

// ---------------------------------------------------------------------------------------------

const ShareCurves: React.FC<{ reductions: RoundReduction[]; side: 'T' | 'CT' }> = ({ reductions, side }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const maxSecs = useMemo(() => Math.max(1, ...reductions.map(r => r.seconds)), [reductions]);

    useEffect(() => {
        const cv = canvasRef.current; if (!cv) return;
        const W = 900, H = 340, padL = 40, padB = 26, padT = 12;
        const dpr = window.devicePixelRatio || 1;
        cv.width = W * dpr; cv.height = H * dpr;
        cv.style.width = `${W}px`; cv.style.height = `${H}px`;
        const ctx = cv.getContext('2d'); if (!ctx) return;
        ctx.scale(dpr, dpr); ctx.clearRect(0, 0, W, H);
        const plotW = W - padL - 10, plotH = H - padT - padB;

        ctx.strokeStyle = '#262a34'; ctx.fillStyle = '#777'; ctx.font = '11px system-ui, sans-serif';
        for (let p = 0; p <= 100; p += 25) {
            const y = padT + plotH - (p / 100) * plotH;
            ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - 10, y); ctx.stroke();
            ctx.fillText(`${p}%`, 6, y + 4);
        }
        for (let s = 0; s <= maxSecs; s += 15) {
            const x = padL + (s / maxSecs) * plotW;
            ctx.fillText(`${s}s`, x - 8, H - 8);
        }

        for (const r of reductions) {
            const arr = side === 'T' ? r.shareT : r.shareCT;
            ctx.beginPath();
            for (let s = 0; s < r.seconds; s++) {
                const x = padL + (s / maxSecs) * plotW;
                const y = padT + plotH - Math.min(1, arr[s]) * plotH;
                if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.strokeStyle = r.won === true ? 'rgba(107,191,107,0.55)' : r.won === false ? 'rgba(196,106,106,0.55)' : 'rgba(150,150,150,0.4)';
            ctx.lineWidth = 1.5; ctx.stroke();
        }
    }, [reductions, side, maxSecs]);

    return (
        <div>
            <canvas ref={canvasRef} />
            <div style={{ marginTop: 10, color: '#777', fontSize: 12, maxWidth: 780, lineHeight: 1.5 }}>
                Share of the map held by {side}, one line per round — green won, red lost. Two things to keep
                in mind: control is partly <em>caused</em> by being alive, so a curve collapsing after a 5v3 is
                partly reading the scoreboard back to you; and this share is not comparable across maps.
            </div>
        </div>
    );
};

export default MapControlPanel;

// ---------------------------------------------------------------------------------------------
// Map views. The table says WHAT, these say WHERE.

type MapMode = 'ownership' | 'contested' | 'timing' | 'conceded';

export const ControlMapView: React.FC<{
    reductions: RoundReduction[];
    cells: Int32Array;
    grid: ControlGrid;
    imageSrc: string;
    side: 'T' | 'CT';
    mode: MapMode;
    window?: [number, number] | null;
    onSelect?: (cellIdx: number[]) => void;
    selection?: number[];
}> = ({ reductions, cells, grid, imageSrc, side, mode, window: win, onSelect, selection }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [img, setImg] = useState<HTMLImageElement | null>(null);

    useEffect(() => {
        if (!imageSrc) return;
        const im = new Image();
        im.onload = () => setImg(im);
        im.src = imageSrc;
    }, [imageSrc]);

    useEffect(() => {
        const cv = canvasRef.current; if (!cv || !img) return;
        const S = 760;
        const dpr = window.devicePixelRatio || 1;
        cv.width = S * dpr; cv.height = S * dpr;
        cv.style.width = `${S}px`; cv.style.height = `${S}px`;
        const ctx = cv.getContext('2d'); if (!ctx) return;
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, S, S);
        ctx.globalAlpha = 0.55;                 // dim the radar so the overlay reads
        ctx.drawImage(img, 0, 0, S, S);
        ctx.globalAlpha = 1;

        const M = cells.length;
        const { G, cell, cfg } = grid;
        const px = (cell / cfg.scale) * (S / 1024);

        // accumulate across rounds
        const val = new Float32Array(M);
        const has = new Uint8Array(M);
        if (mode === 'timing') {
            // median second at which this side first hard-held each cell
            const per: number[][] = Array.from({ length: M }, () => []);
            for (const r of reductions) {
                const arr = side === 'T' ? r.cellFirstT : r.cellFirstCT;
                for (let c = 0; c < M; c++) if (arr[c] >= 0) per[c].push(arr[c]);
            }
            for (let c = 0; c < M; c++) {
                if (per[c].length < Math.max(2, reductions.length * 0.25)) continue;
                per[c].sort((a, b) => a - b);
                val[c] = per[c][per[c].length >> 1];
                has[c] = 1;
            }
        } else if (win) {
            // Real slice, from the per-cell time buckets - not the whole round scaled down.
            const b0 = Math.max(0, Math.min(NUM_BUCKETS - 1, Math.floor(win[0] / BUCKET_SECS)));
            const b1 = Math.max(b0, Math.min(NUM_BUCKETS - 1, Math.floor((win[1] - 1) / BUCKET_SECS)));
            let denom = 0;
            // each coarse bucket aggregates BUCKET_CELL_STRIDE^2 cells, so its counts are that much larger
            const coarseFactor = 4;
            for (const r of reductions) for (let b = b0; b <= b1; b++) denom += r.samplesB[b] * coarseFactor;
            if (!denom) return;
            for (let c = 0; c < M; c++) {
                let t = 0, ct = 0, x = 0;
                for (const r of reductions) {
                    const cc = r.coarseOf[c];
                    for (let b = b0; b <= b1; b++) {
                        const k = cc * NUM_BUCKETS + b;
                        t += r.cellTB[k]; ct += r.cellCTB[k]; x += r.cellConB[k];
                    }
                }
                if (mode === 'contested') { val[c] = x / denom; has[c] = 1; }
                else if (mode === 'ownership') {
                    const own = t + ct;
                    if (own > denom * 0.05) { val[c] = (t - ct) / own; has[c] = 1; }
                } else {
                    const mine = (side === 'T' ? t : ct) / denom, theirs = (side === 'T' ? ct : t) / denom;
                    if (mine >= 0.08 && theirs >= 0.08 && x / denom < 0.01) { val[c] = mine; has[c] = 1; }
                }
            }
        } else {
            let totalSamples = 0;
            for (const r of reductions) totalSamples += r.samples;
            if (!totalSamples) return;
            for (let c = 0; c < M; c++) {
                let t = 0, ct = 0, x = 0;
                for (const r of reductions) { t += r.cellT[c]; ct += r.cellCT[c]; x += r.cellContested[c]; }
                if (mode === 'contested') { val[c] = x / totalSamples; has[c] = 1; }
                else if (mode === 'ownership') {
                    const own = t + ct;
                    if (own > totalSamples * 0.05) { val[c] = (t - ct) / own; has[c] = 1; }   // -1 CT .. +1 T
                } else {
                    // Conceded: ground WE genuinely held that they also ended up holding, with
                    // effectively no fight over it.
                    //
                    // Both "meaningfully held" gates are load-bearing. Scoring theirs/mine without
                    // them peaked on ground the ENEMY owns and we merely brushed past - it lit up the
                    // whole of heaven on nuke, which T never held in the first place.
                    const mine = (side === 'T' ? t : ct) / totalSamples;
                    const theirs = (side === 'T' ? ct : t) / totalSamples;
                    const fought = x / totalSamples;
                    if (mine >= 0.08 && theirs >= 0.08 && fought < 0.01) {
                        // Intensity is OUR holding time, not the ratio. Scoring by theirs/(mine+theirs)
                        // peaks where the enemy dominated and we merely passed through, which lit up
                        // heaven on nuke - ground T never held. What we want is ground we really had
                        // and quietly stopped having.
                        val[c] = mine;
                        has[c] = 1;
                    }
                }
            }
        }

        let maxV = 0;
        for (let c = 0; c < M; c++) if (has[c] && val[c] > maxV) maxV = val[c];

        for (let i = 0; i < M; i++) {
            if (!has[i]) continue;
            const c = cells[i], gx = c % G, gy = (c - (c % G)) / G;
            const x = gx * px, y = gy * px;
            let fill = '';
            if (mode === 'ownership') {
                const v = val[i];                       // +1 all T, -1 all CT
                const a = 0.15 + Math.abs(v) * 0.55;
                fill = v >= 0 ? `rgba(222,155,53,${a})` : `rgba(93,121,174,${a})`;
            } else if (mode === 'contested') {
                const v = maxV > 0 ? val[i] / maxV : 0;
                if (v < 0.04) continue;
                fill = `rgba(200,60,64,${0.08 + v * 0.72})`;
            } else if (mode === 'timing') {
                const v = Math.min(1, val[i] / 60);      // 0s bright, 60s+ dark
                fill = `rgba(${Math.round(250 - v * 120)},${Math.round(210 - v * 150)},${Math.round(90 + v * 90)},${0.75 - v * 0.3})`;
            } else {
                const v = maxV > 0 ? val[i] / maxV : 0;   // relative to the most-held conceded ground
                fill = `rgba(214,150,60,${0.12 + v * 0.63})`;
            }
            ctx.fillStyle = fill;
            ctx.fillRect(x, y, px + 0.6, px + 0.6);
        }
        // Selected ground, drawn ON the map. Reporting a selection only in the panel underneath left
        // you unable to see what you had actually picked.
        if (selection && selection.length) {
            const sel = new Set(selection);
            let mnx = Infinity, mny = Infinity, mxx = -Infinity, mxy = -Infinity;
            ctx.fillStyle = 'rgba(255,255,255,0.18)';
            for (let i = 0; i < M; i++) {
                if (!sel.has(i)) continue;
                const c = cells[i], gx = c % G, gy = (c - (c % G)) / G;
                const x = gx * px, y = gy * px;
                ctx.fillRect(x, y, px + 0.6, px + 0.6);
                if (x < mnx) mnx = x; if (y < mny) mny = y;
                if (x + px > mxx) mxx = x + px; if (y + px > mxy) mxy = y + px;
            }
            if (mnx < Infinity) {
                ctx.strokeStyle = 'rgba(255,255,255,0.9)';
                ctx.lineWidth = 1.5;
                ctx.strokeRect(mnx, mny, mxx - mnx, mxy - mny);
            }
        }

        // `win` is a dependency: without it the map keeps rendering the whole round after the
        // timeline is focused, which looks exactly like the filter silently doing nothing.
    }, [img, reductions, cells, grid, side, mode, win, selection]);

    // drag a box to select ground
    const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
    const S = 760;
    const toLocal = (e: React.MouseEvent) => {
        const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const commit = (box: { x0: number; y0: number; x1: number; y1: number }) => {
        if (!onSelect) return;
        const { G, cell, cfg } = grid;
        const px = (cell / cfg.scale) * (S / 1024);
        const lo = { x: Math.min(box.x0, box.x1), y: Math.min(box.y0, box.y1) };
        const hi = { x: Math.max(box.x0, box.x1), y: Math.max(box.y0, box.y1) };
        if (hi.x - lo.x < 4 || hi.y - lo.y < 4) { onSelect([]); return; }
        const picked: number[] = [];
        for (let i = 0; i < cells.length; i++) {
            const c = cells[i], gx = c % G, gy = (c - (c % G)) / G;
            const x = gx * px, y = gy * px;
            if (x >= lo.x - px && x <= hi.x && y >= lo.y - px && y <= hi.y) picked.push(i);
        }
        onSelect(picked);
    };

    return (
        <div style={{ position: 'relative', width: S, height: S }}>
            <canvas ref={canvasRef} style={{ borderRadius: 8, background: '#12141a' }} />
            <div
                style={{ position: 'absolute', inset: 0, cursor: 'crosshair' }}
                onMouseDown={e => { const p = toLocal(e); setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); }}
                onMouseMove={e => { if (!drag) return; const p = toLocal(e); setDrag({ ...drag, x1: p.x, y1: p.y }); }}
                // Take the end point from the mouseup event rather than from accumulated mousemove
                // state: a fast drag (or a synthetic one) can deliver no intermediate moves at all,
                // leaving a zero-width box that gets thrown away and looks like the drag did nothing.
                onMouseUp={e => { if (drag) { const p = toLocal(e); commit({ ...drag, x1: p.x, y1: p.y }); } setDrag(null); }}
                onMouseLeave={e => { if (drag) { const p = toLocal(e); commit({ ...drag, x1: p.x, y1: p.y }); setDrag(null); } }}
            />
            {drag && (
                <div style={{
                    position: 'absolute', pointerEvents: 'none',
                    left: Math.min(drag.x0, drag.x1), top: Math.min(drag.y0, drag.y1),
                    width: Math.abs(drag.x1 - drag.x0), height: Math.abs(drag.y1 - drag.y0),
                    border: '1.5px solid #e08a2e', background: 'rgba(224,138,46,0.12)', borderRadius: 3,
                }} />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------------------------
// Timeline: when is the map actually being fought over, and which rounds drove it.
// Click a second to jump to the round that contributed most contest at that moment.

export const ContestTimeline: React.FC<{
    reductions: RoundReduction[];
    onPickRound?: (key: string) => void;
    window: [number, number] | null;
    setWindow: (w: [number, number] | null) => void;
}> = ({ reductions, onPickRound, window: win, setWindow }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const W = 900, H = 150, padL = 34, padB = 22, padT = 8;

    const maxSecs = useMemo(() => Math.max(1, ...reductions.map(r => r.seconds)), [reductions]);
    const mean = useMemo(() => {
        const sum = new Float32Array(maxSecs), n = new Float32Array(maxSecs);
        for (const r of reductions) {
            for (let s = 0; s < r.seconds && s < maxSecs; s++) { sum[s] += r.shareContested[s]; n[s]++; }
        }
        const out = new Float32Array(maxSecs);
        for (let s = 0; s < maxSecs; s++) out[s] = n[s] ? sum[s] / n[s] : 0;
        return out;
    }, [reductions, maxSecs]);

    useEffect(() => {
        const cv = canvasRef.current; if (!cv) return;
        const dpr = globalThis.devicePixelRatio || 1;
        cv.width = W * dpr; cv.height = H * dpr;
        cv.style.width = `${W}px`; cv.style.height = `${H}px`;
        const ctx = cv.getContext('2d'); if (!ctx) return;
        ctx.scale(dpr, dpr); ctx.clearRect(0, 0, W, H);
        const plotW = W - padL - 10, plotH = H - padT - padB;
        let peak = 0; for (let s = 0; s < maxSecs; s++) peak = Math.max(peak, mean[s]);
        if (peak <= 0) peak = 1;

        if (win) {
            const x0 = padL + (win[0] / maxSecs) * plotW, x1 = padL + (win[1] / maxSecs) * plotW;
            ctx.fillStyle = 'rgba(224,138,46,0.13)';
            ctx.fillRect(x0, padT, x1 - x0, plotH);
        }
        ctx.strokeStyle = '#262a34';
        for (let s = 0; s <= maxSecs; s += 15) {
            const x = padL + (s / maxSecs) * plotW;
            ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, padT + plotH); ctx.stroke();
            ctx.fillStyle = '#777'; ctx.font = '11px system-ui, sans-serif';
            ctx.fillText(`${s}s`, x - 8, H - 6);
        }
        ctx.beginPath();
        ctx.moveTo(padL, padT + plotH);
        for (let s = 0; s < maxSecs; s++) {
            const x = padL + (s / maxSecs) * plotW;
            const y = padT + plotH - (mean[s] / peak) * plotH;
            ctx.lineTo(x, y);
        }
        ctx.lineTo(padL + plotW, padT + plotH); ctx.closePath();
        ctx.fillStyle = 'rgba(200,60,64,0.35)'; ctx.fill();
        ctx.strokeStyle = 'rgba(220,90,94,0.95)'; ctx.lineWidth = 1.5;
        ctx.beginPath();
        for (let s = 0; s < maxSecs; s++) {
            const x = padL + (s / maxSecs) * plotW;
            const y = padT + plotH - (mean[s] / peak) * plotH;
            if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.fillStyle = '#777'; ctx.font = '11px system-ui, sans-serif';
        ctx.fillText(`${(peak * 100).toFixed(1)}%`, 4, padT + 10);
        ctx.fillText('0', 4, padT + plotH);
    }, [mean, maxSecs, win]);

    const secondAt = (clientX: number): number => {
        const cv = canvasRef.current; if (!cv) return 0;
        const rect = cv.getBoundingClientRect();
        const plotW = W - padL - 10;
        return Math.max(0, Math.min(maxSecs - 1, Math.round(((clientX - rect.left - padL) / plotW) * maxSecs)));
    };

    return (
        <div>
            <canvas
                ref={canvasRef}
                style={{ cursor: 'crosshair', borderRadius: 6, background: '#12141a' }}
                onClick={e => {
                    // Focus only. Jumping straight into a round on a stray click would throw the
                    // user out of this view; opening one is a separate, deliberate button below.
                    const s = secondAt(e.clientX);
                    const b = Math.min(NUM_BUCKETS - 1, Math.floor(s / BUCKET_SECS));
                    setWindow([b * BUCKET_SECS, (b + 1) * BUCKET_SECS]);
                }}
            />
            <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center', color: '#777', fontSize: 12 }}>
                <span>Average share of the map being fought over, by round second.</span>
                {win
                    ? <>
                        <button onClick={() => setWindow(null)}
                            style={{ padding: '3px 10px', borderRadius: 4, border: '1px solid #e08a2e', background: 'transparent', color: '#e08a2e', cursor: 'pointer', fontSize: 12 }}>
                            map showing {win[0]}–{win[1]}s · clear
                        </button>
                        {onPickRound && (() => {
                            // the round with the most contest inside the focused window
                            let best: RoundReduction | null = null, bestV = -1;
                            for (const r of reductions) {
                                let v = 0;
                                for (let s = win[0]; s < Math.min(win[1], r.seconds); s++) v += r.shareContested[s];
                                if (v > bestV) { bestV = v; best = r; }
                            }
                            if (!best || bestV <= 0) return null;
                            const pick = best;
                            return (
                                <button onClick={() => onPickRound(pick.key)}
                                    style={{ padding: '3px 10px', borderRadius: 4, border: '1px solid #3a3f4b', background: 'transparent', color: '#bbb', cursor: 'pointer', fontSize: 12 }}>
                                    watch round {pick.roundNumber} — most fought over here
                                </button>
                            );
                        })()}
                    </>
                    : <span>Click a moment to focus the map on it.</span>}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------------------------
// Region probe: drag a box on the map, get when that ground is taken, how long it is held, and when
// it is given up — per round, across the whole selection. This is the "spot what happens often" tool.

export interface RegionStat {
    /** steamIds of this side standing in the region when it turned - presence, not credit */
    who: string[];
    roundNumber: number;
    key: string;
    won: boolean | null;
    takenAt: number | null;     // second our side first hard-held a majority of the selection
    leftAt: number | null;      // last second we held it
    heldSecs: number;           // seconds we held it at all
    theirsSecs: number;
    contestedSecs: number;
}

export function probeRegion(
    reductions: RoundReduction[],
    cellIdx: number[],
    side: 'T' | 'CT',
    who?: (key: string, atSecond: number) => string[],
): RegionStat[] {
    if (!cellIdx.length) return [];
    return reductions.map(r => {
        const first = side === 'T' ? r.cellFirstT : r.cellFirstCT;
        const last = side === 'T' ? r.cellLastT : r.cellLastCT;
        const mineC = side === 'T' ? r.cellT : r.cellCT;
        const theirsC = side === 'T' ? r.cellCT : r.cellT;

        // "taken" = the second by which half the selected ground had been held at least once,
        // which is far steadier than the first cell to flick over.
        const firsts: number[] = [];
        let fromStart = 0;
        for (const c of cellIdx) {
            if (first[c] === -2) fromStart++;
            else if (first[c] >= 0) firsts.push(first[c]);
        }
        firsts.sort((a, b) => a - b);
        const need = Math.ceil(cellIdx.length / 2) - fromStart;
        const takenAt = fromStart >= Math.ceil(cellIdx.length / 2) ? 0
            : (need > 0 && firsts.length >= need ? firsts[need - 1] : null);

        let leftAt = -1, mine = 0, theirs = 0, con = 0;
        for (const c of cellIdx) {
            if (last[c] > leftAt) leftAt = last[c];
            mine += mineC[c]; theirs += theirsC[c]; con += r.cellContested[c];
        }
        const perCell = cellIdx.length;
        // samples are ~4/s; convert to seconds of wall clock for readability
        const toSecs = (v: number) => Math.round((v / perCell) / 4);
        return {
            who: takenAt !== null && who ? who(r.key, takenAt) : [],
            roundNumber: r.roundNumber, key: r.key, won: r.won,
            takenAt, leftAt: leftAt >= 0 ? leftAt : null,
            heldSecs: toSecs(mine), theirsSecs: toSecs(theirs), contestedSecs: toSecs(con),
        };
    });
}

export const RegionPanel: React.FC<{
    stats: RegionStat[];
    side: 'T' | 'CT';
    cellCount: number;
    nameOf?: (steamId: string) => string;
    onOpenRound?: (key: string) => void;
    onClear: () => void;
}> = ({ stats, side, cellCount, nameOf, onOpenRound, onClear }) => {
    const taken = stats.filter(s => s.takenAt !== null);
    const takenTimes = taken.map(s => s.takenAt!).sort((a, b) => a - b);
    const leftTimes = stats.filter(s => s.leftAt !== null).map(s => s.leftAt!).sort((a, b) => a - b);
    const heldTimes = stats.map(s => s.heldSecs).sort((a, b) => a - b);
    const med = (a: number[]) => (a.length ? a[a.length >> 1] : null);
    const col = side === 'T' ? T_COL : CT_COL;

    return (
        <div style={{ marginTop: 12, borderTop: '1px solid #2f3440', paddingTop: 12 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 8 }}>
                <strong style={{ color: '#ddd' }}>Selected ground</strong>
                <span style={{ color: '#777', fontSize: 12 }}>{cellCount} cells</span>
                <span style={{ color: col }}>held in {taken.length} of {stats.length} rounds</span>
                {med(takenTimes) !== null && <span style={{ color: '#bbb' }}>taken by {med(takenTimes)}s</span>}
                {med(heldTimes) !== null && <span style={{ color: '#bbb' }}>held ~{med(heldTimes)}s</span>}
                {med(leftTimes) !== null && <span style={{ color: '#bbb' }}>last held {med(leftTimes)}s</span>}
                {(() => {
                    const tally = new Map<string, number>();
                    for (const s of stats) for (const id of s.who) tally.set(id, (tally.get(id) ?? 0) + 1);
                    const top = Array.from(tally).sort((a, b) => b[1] - a[1]).slice(0, 3);
                    if (!top.length) return null;
                    return (
                        <span style={{ color: '#bbb' }}>
                            usually {top.map(([id, n]) => `${nameOf ? nameOf(id) : id} (${n})`).join(', ')}
                        </span>
                    );
                })()}
                <button onClick={onClear}
                    style={{ padding: '3px 10px', borderRadius: 4, border: '1px solid #3a3f4b', background: 'transparent', color: '#bbb', cursor: 'pointer', fontSize: 12 }}>
                    clear selection
                </button>
            </div>
            <div style={{ maxHeight: 210, overflowY: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '62px 78px 70px 82px 82px 1fr 60px', gap: 6, color: '#777', fontSize: 11, padding: '3px 2px', position: 'sticky', top: 0, background: '#171a21' }}>
                    <div>Round</div><div>Taken</div><div>Held</div><div>Last held</div><div>Contested</div><div>Who was there</div><div />
                </div>
                {stats.map(s => (
                    <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '62px 78px 70px 82px 82px 1fr 60px', gap: 6, fontSize: 12, padding: '4px 2px', borderBottom: '1px solid #23262f', alignItems: 'center' }}>
                        <div style={{ color: s.won === true ? '#6bbf6b' : s.won === false ? '#c46a6a' : '#888' }}>R{s.roundNumber}</div>
                        <div style={{ color: s.takenAt === null ? '#555' : col }}>{s.takenAt === null ? 'never' : `${s.takenAt}s`}</div>
                        <div style={{ color: '#bbb' }}>{s.heldSecs}s</div>
                        <div style={{ color: '#bbb' }}>{s.leftAt === null ? '—' : `${s.leftAt}s`}</div>
                        <div style={{ color: s.contestedSecs ? '#c46a6a' : '#555' }}>{s.contestedSecs}s</div>
                        <div style={{ color: s.who.length ? '#cfd6e4' : '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={s.who.map(id => (nameOf ? nameOf(id) : id)).join(', ')}>
                            {s.who.length ? s.who.map(id => (nameOf ? nameOf(id) : id)).join(', ') : '—'}
                        </div>
                        <div>
                            {onOpenRound && (
                                <button onClick={() => onOpenRound(s.key)}
                                    style={{ padding: '1px 8px', borderRadius: 4, border: '1px solid #3a3f4b', background: 'transparent', color: '#8fa6c8', cursor: 'pointer', fontSize: 11 }}>
                                    watch
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------------------------
// Two-handle time range over the round clock, matching how the heatmap is filtered. Round clock runs
// DOWN from 1:55, so the labels are inverted relative to the underlying seconds-elapsed values.

const ROUND_CLOCK_SECS = 115;

const clockLabel = (elapsed: number): string => {
    const remain = Math.max(0, ROUND_CLOCK_SECS - elapsed);
    const m = Math.floor(remain / 60), s = Math.floor(remain % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

export const TimeRange: React.FC<{
    maxSecs: number;
    window: [number, number] | null;
    setWindow: (w: [number, number] | null) => void;
}> = ({ maxSecs, window: win, setWindow }) => {
    const lo = win ? win[0] : 0;
    const hi = win ? win[1] : maxSecs;
    const set = (a: number, b: number) => {
        const s = Math.max(0, Math.min(a, maxSecs));
        const e = Math.max(s + BUCKET_SECS, Math.min(b, maxSecs));
        setWindow(s <= 0 && e >= maxSecs ? null : [s, e]);
    };
    return (
        <div style={{ marginBottom: 12, maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#bbb', fontSize: 12, marginBottom: 4 }}>
                <span>{clockLabel(lo)} <span style={{ color: '#777' }}>to</span> {clockLabel(hi)}</span>
                {win && (
                    <button onClick={() => setWindow(null)}
                        style={{ padding: '1px 8px', borderRadius: 4, border: '1px solid #3a3f4b', background: 'transparent', color: '#bbb', cursor: 'pointer', fontSize: 11 }}>
                        whole round
                    </button>
                )}
            </div>
            <input type="range" min={0} max={maxSecs} value={lo} style={{ width: '100%', accentColor: '#e08a2e' }}
                onChange={e => set(Number(e.target.value), hi)} />
            <input type="range" min={0} max={maxSecs} value={hi} style={{ width: '100%', accentColor: '#e08a2e' }}
                onChange={e => set(lo, Number(e.target.value))} />
            <div style={{ color: '#777', fontSize: 11 }}>
                Filters every map layer below to this slice of the round.
                {win && ` Aggregated in ${BUCKET_SECS}s buckets, so the map covers ${lo}–${hi}s elapsed.`}
            </div>
        </div>
    );
};
